import React from 'react';
import { ChartInfoButton, ChartGuideModal, LoadingSpinner, ErrorMessage } from '../ui';
import { LineChart } from '../charts';
import { useTheme } from '../../contexts/ThemeContext';
import { useTimeFrame } from '../../contexts/TimeFrameContext';
import { getChartColors, getChartBackgroundColor, getChartJsOptions } from '../../utils/themeHelpers';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useClubCategorySales } from '../../hooks/useClubSections';
import { TimePeriod } from '../../types/api';
import ChartControls from '../ui/ChartControls';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';

interface ProductCategorySalesTrendProps {
  branchId?: number;
  timeRange?: TimePeriod;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

interface CategorySalesData {
  monthlyData: any[];
  categories: string[];
}

const ProductCategorySalesTrend: React.FC<ProductCategorySalesTrendProps> = ({
  branchId = 0, // Default to اندرزگو branch
  timeRange,
  onError,
  showLoading = true,
  compact = false
}) => {
  const { currentTheme } = useTheme();
  const { apiTimeFrame } = useTimeFrame();
  const { isModalOpen, currentGuide, openGuide, closeGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  
  // Use provided timeRange or fall back to context
  const effectiveTimeRange = timeRange || apiTimeFrame;
  
  // Fetch category sales data
  const { data: categoryData, isLoading, error } = useClubCategorySales(branchId, effectiveTimeRange);
  
  // Component metadata
  const componentId = 'product-category-sales-trend';
  const componentTitle = 'روند فروش دسته‌بندی محصولات';
  
  // Get chart colors from theme helper
  const chartColors = getChartColors(currentTheme);

  const prepareCategoryTrendData = () => {
    if (!categoryData || !categoryData.monthlyData || categoryData.monthlyData.length === 0) {
      return { labels: [], datasets: [] };
    }

    const { monthlyData, categories } = categoryData;

    // Calculate total quantities for each category to find top categories
    const categoryTotals = categories.map(category => ({
      name: category,
      total: monthlyData.reduce((sum, month) => sum + (month[category] || 0), 0)
    }));

    // Sort by total and take top 8 categories to avoid messy tooltips
    const topCategories = categoryTotals
      .sort((a, b) => b.total - a.total)
      .slice(0, 8)
      .map(cat => cat.name);

    // Create labels from months
    const labels = monthlyData.map(month => month.month.split('-')[0]);

    // Create datasets only for top categories
    const datasets = topCategories.map((category, index) => {
      const colorIndex = index % chartColors.length;
      const color = chartColors[colorIndex];
      
      return {
        label: category,
        data: monthlyData.map(month => month[category] || 0),
        borderColor: color,
        backgroundColor: getChartBackgroundColor(colorIndex, currentTheme, 0.2),
        borderWidth: 3,
        pointRadius: 6, 
        pointHoverRadius: 8,
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointHoverBackgroundColor: color,
        pointHoverBorderColor: color,
        tension: 0.3,
        fill: false
      };
    });

    return { labels, datasets };
  };

  const getCategoryInfo = () => {
    if (!categoryData || !categoryData.categories) return { total: 0, showing: 0, topCategories: [] };
    
    const totalCategories = categoryData.categories.length;
    const categoryTotals = categoryData.categories.map(category => ({
      name: category,
      total: categoryData.monthlyData.reduce((sum, month) => sum + (month[category] || 0), 0)
    }));
    
    const topCategories = categoryTotals
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
    
    return {
      total: totalCategories,
      showing: Math.min(totalCategories, 8),
      topCategories: topCategories
    };
  };

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری داده‌های دسته‌بندی محصولات…"
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <ErrorMessage 
          message={`خطا در بارگذاری داده‌های دسته‌بندی محصولات: ${(error as Error).message}`}
        />
      </div>
    );
  }

  if (!categoryData || !categoryData.monthlyData || categoryData.monthlyData.length === 0) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <h3 className="title-card">روند فروش دسته‌بندی محصولات</h3>
        <div className="text-center py-12">
          <p className="text-text-secondary">داده‌ای برای نمایش موجود نیست</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card-base card-padding animate-enter animate-delay-3">
        <ChartInfoButton 
                onClick={() => openGuide(CHART_GUIDES.categorySalesTrend)}
                className="mb-4"
              />
        <h3 className="title-card">{componentTitle}</h3>
        <div className="mb-4">
          <p className="text-text-secondary text-sm text-center">
            نمایش روند ماهانه فروش هر دسته از محصولات بر اساس تعداد فروخته شده
          </p>
        {categoryData && categoryData.categories && categoryData.categories.length > 8 && (
          <div className="mt-2 p-2 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
            <p className="text-xs text-text-secondary">
              💡 برای سادگی مشاهده، {getCategoryInfo().showing} دسته‌بندی برتر از مجموع {getCategoryInfo().total} دسته نمایش داده شده است
            </p>
          </div>
        )}
      </div>
      
      <div style={{ height: compact ? '300px' : '400px' }}>
        <LineChart 
          data={prepareCategoryTrendData()} 
          height={compact ? 300 : 400}
          options={{
            ...getChartJsOptions(currentTheme),
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              legend: {
                ...getChartJsOptions(currentTheme).plugins?.legend,
                display: !compact
              },
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  ...(getChartJsOptions(currentTheme).plugins?.tooltip?.callbacks || {}),
                  label: function(context: any) {
                    const label = context.dataset.label || '';
                    const value = `${context.parsed.y.toLocaleString()} عدد`;
                    const separator = '\u00A0:\u00A0';
                    return `${label}${separator}${value}`;
                  },
                  afterBody: function(context: any) {
                    // Calculate total for this month
                    const total = context.reduce((sum: number, item: any) => sum + item.parsed.y, 0);
                    return [`مجموع: ${total.toLocaleString()} عدد`];
                  }
                },
                filter: function(tooltipItem: any) {
                  // Only show non-zero values to reduce clutter
                  return tooltipItem.parsed.y > 0;
                }
              }
            },
            scales: {
              ...getChartJsOptions(currentTheme).scales,
              y: {
                ...getChartJsOptions(currentTheme).scales?.y,
                beginAtZero: true,
                ticks: {
                  ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                  callback: function(value: any) {
                    return value.toLocaleString();
                  }
                }
              }
            }
          }}
        />
      </div>

      {/* Top Categories Summary */}
      {!compact && categoryData && categoryData.categories && categoryData.categories.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            خلاصه دسته‌بندی‌های برتر (بر اساس کل فروش)
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            {getCategoryInfo().topCategories.map((category, index) => {
              
              return (
                <div 
                  key={category.name} 
                  className="text-center p-2 rounded-lg border"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderColor: chartColors[index % chartColors.length]
                  }}
                >
                  <div 
                    className="w-3 h-3 mx-auto mb-1 rounded-full"
                    style={{ backgroundColor: chartColors[index % chartColors.length] }}
                  ></div>
                  <h5 className="font-bold text-xs mb-1 truncate" 
                      style={{ color: 'var(--text-primary)' }}
                      title={category.name}>
                    {category.name.length > 10 ? `${category.name.substring(0, 10)}...` : category.name}
                  </h5>
                  <p className="text-xs text-text-secondary mb-1">کل</p>
                  <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {category.total.toLocaleString()}
                  </p>
                </div>
              );
            })}
          </div>
          
          {categoryData.categories.length > 8 && (
            <div className="mt-3 text-center">
              <p className="text-xs text-text-secondary">
                + {categoryData.categories.length - 8} دسته‌بندی دیگر که در نمودار نمایش داده نشده‌اند
              </p>
            </div>
          )}
        </div>
      )}
      </div>
      
      {/* Chart Guide Modal */}
      {currentGuide && (
        <ChartGuideModal
          isOpen={isModalOpen}
          onClose={closeGuide}
          title={currentGuide.title}
        >
          <div className="space-y-8">
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">توضیحات</h3>
              <p className="text-text-secondary">{currentGuide.description}</p>
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نحوه تفسیر</h3>
              <p className="text-text-secondary">{currentGuide.interpretation}</p>
            </div>
            {currentGuide.tips.length > 0 && (
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نکات کلیدی</h3>
                <ul className="list-disc list-inside space-y-2">
                  {currentGuide.tips.map((tip, index) => (
                    <li key={index} className="text-text-secondary">{tip}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </ChartGuideModal>
      )}
    </>
  );
};

export default ProductCategorySalesTrend;